/**
 * Device ID Utility
 *
 * Generates and manages unique device IDs for WebSocket connections.
 * Enables multi-device support where a user can be logged in from multiple devices.
 *
 * Each device gets a unique identifier stored in localStorage along with metadata.
 * This allows the backend to push messages to all devices of a user simultaneously.
 */

// Memory cache for device ID (persists during session)
let cachedDeviceId = null;

/**
 * Check if localStorage is available
 */
const isLocalStorageAvailable = () => {
  try {
    const test = "__storage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Generate a unique device identifier
 * Format: [deviceType]-[timestamp]-[randomId]
 * Example: web-1704067200000-a7f3e9b2
 */
const generateUniqueDeviceId = () => {
  const deviceType = "web"; // Could be 'mobile', 'app', 'web', etc.
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substr(2, 8);

  return `${deviceType}-${timestamp}-${randomId}`;
};

/**
 * Get or create a device ID for this client
 *
 * Device ID is stored in localStorage and reused for the same browser/device.
 * This ensures the backend can track the device even if the page is refreshed.
 *
 * @returns {string} Unique device ID for this client
 */
export const getOrCreateDeviceId = () => {
  const DEVICE_ID_KEY = "websocket_device_id";

  // Return cached ID if available (for this session)
  if (cachedDeviceId) {
    return cachedDeviceId;
  }

  try {
    // Try to retrieve from localStorage
    if (isLocalStorageAvailable()) {
      let deviceId = localStorage.getItem(DEVICE_ID_KEY);

      if (!deviceId) {
        // Generate new device ID
        deviceId = generateUniqueDeviceId();
        localStorage.setItem(DEVICE_ID_KEY, deviceId);
        console.log("🆕 New device ID created:", deviceId);
      } else {
        console.log("📱 Using existing device ID:", deviceId);
      }

      // Cache in memory for this session
      cachedDeviceId = deviceId;
      return deviceId;
    }
  } catch (error) {
    console.warn("⚠️  Error accessing localStorage:", error);
  }

  // Fallback: Generate and cache temporary ID for this session
  if (!cachedDeviceId) {
    cachedDeviceId = generateUniqueDeviceId();
    console.warn(
      "⚠️  localStorage unavailable, using temporary device ID for this session:",
      cachedDeviceId,
    );
  }

  return cachedDeviceId;
};

/**
 * Get stored device metadata
 * @returns {Object} Device metadata (type, OS, browser, etc.)
 */
export const getDeviceMetadata = () => {
  try {
    return {
      deviceId: getOrCreateDeviceId(),
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.warn("Error getting device metadata:", error);
    return {
      deviceId: getOrCreateDeviceId(),
      timestamp: new Date().toISOString(),
    };
  }
};

/**
 * Clear device ID (useful on logout)
 */
export const clearDeviceId = () => {
  try {
    localStorage.removeItem("websocket_device_id");
    cachedDeviceId = null; // Clear memory cache
    console.log("🔄 Device ID cleared");
  } catch (error) {
    console.warn("Error clearing device ID:", error);
  }
};

/**
 * Get all devices for a user (if stored locally)
 * @returns {Array} Array of device IDs
 */
export const getStoredDevices = () => {
  try {
    const devicesJson = localStorage.getItem("websocket_user_devices");
    return devicesJson ? JSON.parse(devicesJson) : [];
  } catch (error) {
    return [];
  }
};

/**
 * Store device info when user logs in from multiple devices
 * This helps track which devices are registered
 *
 * @param {string} deviceId - Device ID to store
 */
export const storeDeviceInfo = (deviceId) => {
  try {
    const devices = getStoredDevices();

    if (!devices.includes(deviceId)) {
      devices.push(deviceId);
      localStorage.setItem("websocket_user_devices", JSON.stringify(devices));
      console.log("💾 Stored device info:", deviceId);
    }
  } catch (error) {
    console.warn("Error storing device info:", error);
  }
};

/**
 * Check if device ID is valid (hasn't expired)
 * Device IDs don't expire, but can add expiry logic if needed
 *
 * @param {string} deviceId - Device ID to validate
 * @returns {boolean} True if device ID is valid
 */
export const isValidDeviceId = (deviceId) => {
  return deviceId && typeof deviceId === "string" && deviceId.length > 0;
};

/**
 * Get device type from device ID
 * @param {string} deviceId - Device ID
 * @returns {string} Device type (web, mobile, app, etc.)
 */
export const getDeviceTypeFromId = (deviceId) => {
  const parts = deviceId.split("-");
  return parts[0] || "unknown";
};

export default {
  getOrCreateDeviceId,
  getDeviceMetadata,
  clearDeviceId,
  getStoredDevices,
  storeDeviceInfo,
  isValidDeviceId,
  getDeviceTypeFromId,
};
